import { Routes, Route, Navigate } from 'react-router'
import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { ProtectedRoute } from './ProtectedRoute'
import AdminLayout from '@/components/AdminLayout'
import { AppScopeGuard } from '@/components/AppScopeGuard'
import { getEnvConfig, isProductionRuntime } from '@/lib/runtime-env'

// Loading 组件
const Loading = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <Spin size="large" />
  </div>
)

// 懒加载页面
const Login = lazy(() => import('@/pages/login'))
const AnalyticsReports = lazy(() => import('@/pages/analytics/reports'))
const AnalyticsDashboard = lazy(() => import('@/pages/analytics/dashboard'))
const ServiceMonitor = lazy(() => import('@/pages/service-monitor'))
const CampaignManagement = lazy(() => import('@/pages/campaigns'))
const Users = lazy(() => import('@/pages/users'))
const First24HSwipeStats = lazy(() => import('@/pages/users/first-24h-swipe-stats'))
const DeviceAttributionEvents = lazy(() => import('@/pages/users/device-attribution-events'))
const Reports = lazy(() => import('@/pages/reports'))

// 角色管理
const Characters = lazy(() => import('@/pages/characters'))
const CharacterAudit = lazy(() => import('@/pages/characters/audit'))
const CharacterCategories = lazy(() => import('@/pages/characters/categories'))
const CharacterTags = lazy(() => import('@/pages/characters/tags'))
const CharacterLookingForTags = lazy(() => import('@/pages/characters/looking-for-tags'))
const CharacterTypePreferTags = lazy(() => import('@/pages/characters/type-prefer-tags'))
const TemplateVariables = lazy(() => import('@/pages/characters/template-variables'))
const SystemTemplateVariables = lazy(() => import('@/pages/characters/system-template-variables'))
const CharacterStats = lazy(() => import('@/pages/characters/stats'))

// 支付管理
const Products = lazy(() => import('@/pages/payment/products'))
const Orders = lazy(() => import('@/pages/payment/orders'))
const RechargeUsers = lazy(() => import('@/pages/payment/recharge-users'))
const Subscriptions = lazy(() => import('@/pages/payment/subscriptions'))
const Refunds = lazy(() => import('@/pages/payment/refunds'))
const Fulfillments = lazy(() => import('@/pages/payment/fulfillments'))
const WebhookLogs = lazy(() => import('@/pages/payment/webhook-logs'))
const ApplePayConfigs = lazy(() => import('@/pages/payment/apple-pay-configs'))
const GooglePayConfigs = lazy(() => import('@/pages/payment/google-pay-configs'))
const ThirdPartyPaymentConfigs = lazy(() => import('@/pages/payment/third-party-configs'))
const PaymentRouteConfigs = lazy(() => import('@/pages/payment/route-configs'))
const PaymentTraces = lazy(() => import('@/pages/payment/traces'))

// 运营管理
const Activities = lazy(() => import('@/pages/operations/activities'))
const Announcements = lazy(() => import('@/pages/operations/announcements'))
const Notifications = lazy(() => import('@/pages/operations/notifications'))
const Tasks = lazy(() => import('@/pages/operations/tasks'))
const Checkin = lazy(() => import('@/pages/operations/checkin'))
const Invitation = lazy(() => import('@/pages/operations/invitation'))
const Vip = lazy(() => import('@/pages/operations/vip'))
const TrendingKeywords = lazy(() => import('@/pages/operations/trending-keywords'))
const CharacterGeneration = lazy(() => import('@/pages/operations/character-generation'))
const Rewards = lazy(() => import('@/pages/operations/rewards'))

// 系统设置
const Apps = lazy(() => import('@/pages/settings/apps'))
const Versions = lazy(() => import('@/pages/settings/versions'))
const Firebase = lazy(() => import('@/pages/settings/firebase'))
const ServiceAccounts = lazy(() => import('@/pages/settings/service-accounts'))
const BaseLimits = lazy(() => import('@/pages/settings/base-limits'))
const AlertBots = lazy(() => import('@/pages/settings/alert-bots'))
const AppStoreLinks = lazy(() => import('@/pages/settings/app-store-links'))
const OnelinkConfigs = lazy(() => import('@/pages/settings/onelink-configs'))
const TranslationConfigs = lazy(() => import('@/pages/settings/translation-configs'))
const OperationLogs = lazy(() => import('@/pages/settings/operation-logs'))
const OAuthConfigs = lazy(() => import('@/pages/settings/oauth-configs'))
const AppCosts = lazy(() => import('@/pages/settings/app-costs'))
const FirstLoginRewards = lazy(() => import('@/pages/settings/first-login-rewards'))
const AppVersionReviewRewards = lazy(() => import('@/pages/settings/app-version-review-rewards'))
const ChatAIConfigs = lazy(() => import('@/pages/settings/chat-ai-configs'))
const AITokenUsage = lazy(() => import('@/pages/settings/ai-token-usage'))
const RedisTools = lazy(() => import('@/pages/settings/redis-tools'))
const InteractionSignalCopy = lazy(() => import('@/pages/settings/interaction-signal-copy'))
const EventSystem = lazy(() => import('@/pages/settings/event-system'))

// 视觉生成
const VisualGenerationAppConfigs = lazy(() => import('@/pages/visual-replacement/app-configs'))
const VisualPricing = lazy(() => import('@/pages/visual-replacement/pricing'))
const VisualPromotions = lazy(() => import('@/pages/visual-replacement/promotions'))
const VisualReplacementImageTemplates = lazy(
  () => import('@/pages/visual-replacement/image/templates')
)
const VisualReplacementImageModels = lazy(() => import('@/pages/visual-replacement/image/models'))
const VisualReplacementImagePrompts = lazy(() => import('@/pages/visual-replacement/image/prompts'))
const VisualReplacementVideoTemplates = lazy(
  () => import('@/pages/visual-replacement/video/templates')
)
const VisualReplacementVideoModels = lazy(() => import('@/pages/visual-replacement/video/models'))
const VisualReplacementVideoPrompts = lazy(() => import('@/pages/visual-replacement/video/prompts'))
const VideoStreamingTasks = lazy(() => import('@/pages/visual-replacement/video/streaming'))
const VisualReplacementChatModels = lazy(() => import('@/pages/visual-replacement/chat/models'))
const VisualReplacementChatPrompts = lazy(() => import('@/pages/visual-replacement/chat/prompts'))
const VisualGenerationMonitor = lazy(() => import('@/pages/visual-generation-monitor'))
const VisualGenerationPreview = lazy(() => import('@/pages/visual-generation-preview'))

// 匹配管理
const MatchConfig = lazy(() => import('@/pages/match/config'))
const MatchStats = lazy(() => import('@/pages/match/stats'))
const MatchDashboard = lazy(() => import('@/pages/match/dashboard'))
const MatchDiagnostics = lazy(() => import('@/pages/match/diagnostics'))
const MatchUser = lazy(() => import('@/pages/match/user'))
const MatchCityNeighbors = lazy(() => import('@/pages/match/city-neighbors'))
const MatchResults = lazy(() => import('@/pages/match/results'))
const MatchChattedCharacters = lazy(() => import('@/pages/match/chatted-characters'))
const MatchQuota = lazy(() => import('@/pages/match/quota'))
const AIFemaleUsers = lazy(() => import('@/pages/match/ai-female-users'))
const DeletedAIFemaleUsers = lazy(() => import('@/pages/match/deleted-ai-female-users'))
const AIFemaleUserMoments = lazy(() => import('@/pages/match/ai-female-users/moments'))
const MatchMoments = lazy(() => import('@/pages/match/moments'))
const MatchSwipeRecords = lazy(() => import('@/pages/match/swipe-records'))
const MatchAdminOps = lazy(() => import('@/pages/match/admin-ops'))

// 主播管理
const AnchorGuilds = lazy(() => import('@/pages/anchors/guilds'))
const AnchorGuildInvites = lazy(() => import('@/pages/anchors/guild-invites'))
const AnchorLevels = lazy(() => import('@/pages/anchors/levels'))
const AnchorLevelRatingLogs = lazy(() => import('@/pages/anchors/level-rating-logs'))
const AnchorPool = lazy(() => import('@/pages/anchors/pool'))
const AnchorCallRecords = lazy(() => import('@/pages/anchors/call-records'))
const VideoCallFrameModerations = lazy(() => import('@/pages/anchors/frame-moderations'))
const AnchorOnlineStats = lazy(() => import('@/pages/anchors/online-stats'))
const AnchorWithdrawRequests = lazy(() => import('@/pages/anchors/withdraw-requests'))
const AnchorRevenueDashboard = lazy(() => import('@/pages/anchors/revenue-dashboard'))
const AnchorPrivateMedia = lazy(() => import('@/pages/anchors/private-media'))

// 视频通话
const VideoCallDashboard = lazy(() => import('@/pages/videocall/dashboard'))

// 用户管理
const AdminAccounts = lazy(() => import('@/pages/users/admins'))

// 系统配置
const SystemConfigs = lazy(() => import('@/pages/settings/system-configs'))

// 推送管理
const PushStats = lazy(() => import('@/pages/push/stats'))
const PushTemplates = lazy(() => import('@/pages/push/templates'))
const PushTasksPage = lazy(() => import('@/pages/push/tasks'))
const PushTest = lazy(() => import('@/pages/push/test'))
const PushConfig = lazy(() => import('@/pages/push/config'))
const RecallLogs = lazy(() => import('@/pages/push/recall-logs'))
const UserBehaviorMonitor = lazy(() => import('@/pages/observability/user-behavior-monitor'))
const APIRequestStats = lazy(() => import('@/pages/observability/api-request-stats'))
const AgentHarness = lazy(() => import('@/pages/observability/agent-harness/slim'))
const PromptSampleSets = lazy(() => import('@/pages/chat-ai/prompt-sample-sets'))
const PromptExperiments = lazy(() => import('@/pages/chat-ai/prompt-experiments'))
const PromptReviewCenter = lazy(() => import('@/pages/chat-ai/prompt-annotations'))
const PromptEvaluationCenter = lazy(() => import('@/pages/chat-ai/prompt-evaluation-center'))
const AgentFlows = lazy(() => import('@/pages/chat-ai/agent-flows'))
const AgentFlowNodeCatalogs = lazy(() => import('@/pages/chat-ai/agent-flow-node-catalogs'))
const PromptWorkspace = lazy(() => import('@/pages/chat-ai/prompt-workspace'))

// 聊天管理
const Rooms = lazy(() => import('@/pages/rooms'))
const RoomMessages = lazy(() => import('@/pages/rooms/messages'))
const ChatReplay = lazy(() => import('@/pages/rooms/chat-replay'))
const ChatAssets = lazy(() => import('@/pages/rooms/chat-assets'))
const ChatCorpus = lazy(() => import('@/pages/rooms/chat-corpus'))
const ChatQA = lazy(() => import('@/pages/rooms/chat-qa'))
const UnpaidFollowUpStats = lazy(() => import('@/pages/rooms/unpaid-follow-up-stats'))
const ChatRelationshipStages = lazy(() => import('@/pages/settings/chat-relationship-stages'))

// 其他
const AccessControl = lazy(() => import('@/pages/moderation/access-control'))

const withAppScope = (element: React.ReactNode) => <AppScopeGuard>{element}</AppScopeGuard>

export function AppRoutes() {
  const showApiRequestStats = !isProductionRuntime(getEnvConfig())

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* 公开路由 */}
        <Route path="/login" element={<Login />} />

        {/* 受保护路由 */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/service-monitor" replace />} />
          <Route
            path="analytics/dashboard"
            element={<Navigate to="/analytics/reports/create" replace />}
          />
          <Route path="analytics/reports/create" element={<AnalyticsDashboard />} />
          <Route path="analytics/reports/view" element={<AnalyticsReports />} />
          <Route path="campaigns" element={<Navigate to="/campaigns/overview" replace />} />
          <Route path="campaigns/overview" element={withAppScope(<CampaignManagement view="overview" />)} />
          <Route path="campaigns/records" element={withAppScope(<CampaignManagement view="records" />)} />
          <Route path="campaigns/sites" element={withAppScope(<CampaignManagement view="sites" />)} />
          <Route path="campaigns/rules" element={withAppScope(<CampaignManagement view="rules" />)} />
          <Route path="campaigns/integrations" element={withAppScope(<CampaignManagement view="integrations" />)} />
          <Route path="service-monitor" element={withAppScope(<ServiceMonitor />)} />
          <Route path="users" element={<Users />} />
          <Route path="users/first-24h-swipe-stats" element={<First24HSwipeStats />} />
          <Route
            path="users/device-attribution-events"
            element={withAppScope(<DeviceAttributionEvents />)}
          />
          <Route path="users/admins" element={<AdminAccounts />} />
          <Route path="reports" element={<Reports />} />

          {/* 角色管理 */}
          <Route path="characters" element={<Characters />} />
          <Route path="characters/audit" element={<CharacterAudit />} />
          <Route path="characters/categories" element={<CharacterCategories />} />
          <Route path="characters/tags" element={<CharacterTags />} />
          <Route path="characters/looking-for-tags" element={<CharacterLookingForTags />} />
          <Route path="characters/type-prefer-tags" element={<CharacterTypePreferTags />} />
          <Route path="characters/template-variables" element={<TemplateVariables />} />
          <Route
            path="characters/system-template-variables"
            element={<SystemTemplateVariables />}
          />
          <Route path="characters/stats" element={<CharacterStats />} />

          {/* 支付管理 */}
          <Route path="payment/products" element={withAppScope(<Products />)} />
          <Route path="payment/orders" element={withAppScope(<Orders />)} />
          <Route path="payment/recharge-users" element={withAppScope(<RechargeUsers />)} />
          <Route path="payment/subscriptions" element={withAppScope(<Subscriptions />)} />
          <Route path="payment/refunds" element={withAppScope(<Refunds />)} />
          <Route path="payment/fulfillments" element={withAppScope(<Fulfillments />)} />
          <Route path="payment/webhook-logs" element={withAppScope(<WebhookLogs />)} />
          <Route path="payment/apple-pay-configs" element={withAppScope(<ApplePayConfigs />)} />
          <Route path="payment/google-pay-configs" element={withAppScope(<GooglePayConfigs />)} />
          <Route path="payment/third-party-configs" element={<ThirdPartyPaymentConfigs />} />
          <Route path="payment/route-configs" element={withAppScope(<PaymentRouteConfigs />)} />
          <Route path="payment/traces" element={withAppScope(<PaymentTraces />)} />

          {/* 运营管理 */}
          <Route path="operations/rewards" element={withAppScope(<Rewards />)} />
          <Route path="operations/activities" element={<Activities />} />
          <Route path="operations/announcements" element={<Announcements />} />
          <Route path="operations/notifications" element={<Notifications />} />
          <Route path="operations/tasks" element={withAppScope(<Tasks />)} />
          <Route path="operations/checkin" element={withAppScope(<Checkin />)} />
          <Route path="operations/invitation" element={withAppScope(<Invitation />)} />
          <Route path="operations/vip" element={withAppScope(<Vip />)} />
          <Route path="operations/trending-keywords" element={<TrendingKeywords />} />
          <Route path="operations/character-generation" element={<CharacterGeneration />} />

          {/* 系统设置 */}
          <Route path="settings/apps" element={<Apps />} />
          <Route path="settings/versions" element={<Versions />} />
          <Route path="settings/firebase" element={<Firebase />} />
          <Route path="settings/service-accounts" element={<ServiceAccounts />} />
          <Route path="settings/base-limits" element={<BaseLimits />} />
          <Route path="settings/alert-bots" element={<AlertBots />} />
          <Route path="settings/app-store-links" element={withAppScope(<AppStoreLinks />)} />
          <Route path="settings/onelink-configs" element={withAppScope(<OnelinkConfigs />)} />
          <Route path="settings/translation-configs" element={<TranslationConfigs />} />
          <Route path="settings/oauth-configs" element={<OAuthConfigs />} />
          <Route path="settings/system-configs" element={<SystemConfigs />} />
          <Route path="settings/interaction-signal-copy" element={<InteractionSignalCopy />} />
          <Route path="settings/event-system" element={<EventSystem />} />
          <Route path="settings/chat-ai-configs" element={withAppScope(<ChatAIConfigs />)} />
          <Route path="settings/chat-ai-configs/new" element={withAppScope(<ChatAIConfigs />)} />
          <Route
            path="settings/chat-ai-configs/:profileId"
            element={withAppScope(<ChatAIConfigs />)}
          />
          <Route
            path="settings/chat-strategy"
            element={<Navigate to="/rooms/chat-strategy" replace />}
          />
          <Route path="settings/ai-token-usage" element={withAppScope(<AITokenUsage />)} />
          <Route path="settings/redis-tools" element={<RedisTools />} />
          <Route path="settings/first-login-rewards" element={<FirstLoginRewards />} />
          <Route path="settings/app-version-review-rewards" element={withAppScope(<AppVersionReviewRewards />)} />
          <Route path="settings/operation-logs" element={<OperationLogs />} />
          <Route path="settings/app-costs" element={withAppScope(<AppCosts />)} />

          {/* 视觉生成 */}
          <Route path="visual-replacement/pricing" element={withAppScope(<VisualPricing />)} />
          <Route path="visual-replacement/promotions" element={withAppScope(<VisualPromotions />)} />
          <Route
            path="visual-replacement/app-configs"
            element={withAppScope(<VisualGenerationAppConfigs />)}
          />
          <Route
            path="visual-replacement/image/templates"
            element={withAppScope(<VisualReplacementImageTemplates />)}
          />
          <Route
            path="visual-replacement/image/models"
            element={withAppScope(<VisualReplacementImageModels />)}
          />
          <Route
            path="visual-replacement/image/prompts"
            element={withAppScope(<VisualReplacementImagePrompts />)}
          />
          <Route
            path="visual-replacement/video/templates"
            element={withAppScope(<VisualReplacementVideoTemplates />)}
          />
          <Route
            path="visual-replacement/video/models"
            element={withAppScope(<VisualReplacementVideoModels />)}
          />
          <Route
            path="visual-replacement/video/prompts"
            element={withAppScope(<VisualReplacementVideoPrompts />)}
          />
          <Route
            path="visual-replacement/video/streaming"
            element={withAppScope(<VideoStreamingTasks />)}
          />
          <Route
            path="visual-replacement/chat/models"
            element={withAppScope(<VisualReplacementChatModels />)}
          />
          <Route
            path="visual-replacement/chat/prompts"
            element={withAppScope(<VisualReplacementChatPrompts />)}
          />
          <Route
            path="visual-generation/tasks"
            element={withAppScope(<VisualGenerationMonitor />)}
          />
          <Route path="visual-generation/preview" element={<VisualGenerationPreview />} />

          {/* 匹配管理 */}
          <Route path="match/dashboard" element={withAppScope(<MatchDashboard />)} />
          <Route path="match/ai-female-users" element={withAppScope(<AIFemaleUsers />)} />
          <Route
            path="match/deleted-ai-female-users"
            element={withAppScope(<DeletedAIFemaleUsers />)}
          />
          <Route
            path="match/ai-female-users/moments"
            element={withAppScope(<AIFemaleUserMoments />)}
          />
          <Route
            path="match/ai-female-users/:id/moments"
            element={withAppScope(<AIFemaleUserMoments />)}
          />
          <Route path="match/moments" element={withAppScope(<MatchMoments />)} />
          <Route path="match/user" element={withAppScope(<MatchUser />)} />
          <Route path="match/config" element={withAppScope(<MatchConfig />)} />
          <Route path="match/stats" element={withAppScope(<MatchStats />)} />
          <Route path="match/diagnostics" element={withAppScope(<MatchDiagnostics />)} />
          <Route path="match/city-neighbors" element={withAppScope(<MatchCityNeighbors />)} />
          <Route path="match/results" element={withAppScope(<MatchResults />)} />
          <Route
            path="match/chatted-characters"
            element={withAppScope(<MatchChattedCharacters />)}
          />
          <Route path="match/swipe-records" element={withAppScope(<MatchSwipeRecords />)} />
          <Route path="match/quota" element={withAppScope(<MatchQuota />)} />
          <Route path="match/admin-ops" element={withAppScope(<MatchAdminOps />)} />

          {/* 主播管理 */}
          <Route path="anchors/guilds" element={<AnchorGuilds />} />
          <Route path="anchors/guild-invites" element={<AnchorGuildInvites />} />
          <Route path="anchors/levels" element={<AnchorLevels />} />
          <Route path="anchors/level-rating-logs" element={<AnchorLevelRatingLogs />} />
          <Route path="anchors/pool" element={withAppScope(<AnchorPool />)} />
          <Route path="anchors/call-records" element={withAppScope(<AnchorCallRecords />)} />
          <Route
            path="anchors/frame-moderations"
            element={withAppScope(<VideoCallFrameModerations />)}
          />
          <Route path="anchors/online-stats" element={withAppScope(<AnchorOnlineStats />)} />
          <Route path="anchors/withdraw-requests" element={<AnchorWithdrawRequests />} />
          <Route path="anchors/private-media" element={<AnchorPrivateMedia />} />
          <Route path="anchors/revenue-dashboard" element={<AnchorRevenueDashboard />} />

          {/* 视频通话 */}
          <Route path="videocall/dashboard" element={withAppScope(<VideoCallDashboard />)} />

          {/* 推送管理 */}
          <Route path="push/stats" element={withAppScope(<PushStats />)} />
          <Route path="push/templates" element={<PushTemplates />} />
          <Route path="push/tasks" element={<PushTasksPage />} />
          <Route path="push/test" element={<PushTest />} />
          <Route path="push/config" element={<PushConfig />} />
          <Route path="push/recall-logs" element={<RecallLogs />} />
          <Route
            path="observability/user-behavior-monitor"
            element={withAppScope(<UserBehaviorMonitor />)}
          />
          {showApiRequestStats ? (
            <Route
              path="observability/api-request-stats"
              element={withAppScope(<APIRequestStats />)}
            />
          ) : null}

          {/* Agent Harness */}
          <Route path="agent-harness" element={<Navigate to="/agent-harness/runs" replace />} />
          <Route path="agent-harness/runs" element={withAppScope(<AgentHarness view="runs" />)} />
          <Route
            path="agent-harness/config"
            element={withAppScope(<AgentHarness view="config" />)}
          />

          {/* Chat AI Prompt experiments */}
          <Route path="chat-ai/prompt-workspace" element={<PromptWorkspace />} />
          <Route path="chat-ai/prompt-sample-sets" element={<PromptSampleSets />} />
          <Route path="chat-ai/prompt-experiments" element={<PromptExperiments />} />
          <Route path="chat-ai/agent-flows" element={<AgentFlows />} />
          <Route path="chat-ai/agent-flow-node-catalogs" element={<AgentFlowNodeCatalogs />} />
          <Route
            path="chat-ai/prompt-annotations"
            element={<Navigate to="/chat-ai/prompt-review-center" replace />}
          />
          <Route path="chat-ai/prompt-review-center" element={<PromptReviewCenter />} />
          <Route path="chat-ai/prompt-evaluation-center" element={<PromptEvaluationCenter />} />

          {/* 聊天管理 */}
          <Route path="rooms" element={<Rooms />} />
          <Route path="rooms/messages" element={<RoomMessages />} />
          <Route path="rooms/chat-replay" element={<ChatReplay />} />
          <Route path="rooms/chat-assets" element={<ChatAssets />} />
          <Route path="rooms/chat-corpus" element={<ChatCorpus />} />
          <Route path="rooms/chat-qa" element={<ChatQA />} />
          <Route
            path="rooms/unpaid-follow-up-stats"
            element={withAppScope(<UnpaidFollowUpStats />)}
          />
          <Route path="rooms/chat-relationship-stages" element={<ChatRelationshipStages />} />

          {/* 其他 */}
          <Route path="moderation/access-control" element={<AccessControl />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/service-monitor" replace />} />
      </Routes>
    </Suspense>
  )
}
